import { QuestionCircleOutlined } from '@ant-design/icons'
import { Alert, DatePicker, Input, Radio, Upload } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import cn from 'classnames'
import moment from 'moment'
import { useState } from 'react'
import MilestoneInput from './MilestoneInput'

const QuestionView = ({ currentQuestion, formik }) => {
  if (!currentQuestion) {
    return null
  }

  const {
    key,
    title,
    description,
    type,
    minLength,
    maxLength,
    options: optRaw,
    explanation,
  } = currentQuestion

  const options = typeof optRaw === 'string' ? JSON.parse(optRaw) : optRaw

  return (
    <div key={key} className="wbso-question flex w-full flex-col gap-y-4">
      <h3 className="text-2xl font-semibold md:text-4xl">{title}</h3>
      <p>{description}</p>

      {type === 'String' && !options && (
        <div className="relative">
          <Input
            type="text"
            name={key}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[key]}
            style={{ width: '100%' }}
            maxLength={maxLength}
            minLength={minLength}
            className="pr-20"
          />
          <div className="absolute right-3 top-4 text-sm font-semibold">
            {formik.values[key]?.length ?? 0}/{maxLength}
          </div>
        </div>
      )}
      {type === 'LongText' && key !== 'phase_description' && (
        <>
          <TextArea
            name={key}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[key]}
            rows="5"
            style={{ width: '100%' }}
            maxLength={maxLength}
            minLength={minLength}
          />
          <div className="mt-1">
            {formik.values[key]?.length ?? 0}/{maxLength}
          </div>
        </>
      )}
      {type === 'Enum' && (
        <Radio.Group
          name={key}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values[key]}
        >
          {options.map((option) => (
            <Radio
              key={option}
              className="block accent-g400 text-base"
              value={option}
            >
              {option}
            </Radio>
          ))}
        </Radio.Group>
      )}
      {type === 'Date' && (
        <DatePicker
          picker="month"
          format={'MM-YYYY'}
          name={key}
          onChange={(value) => {
            const firstDayOfMonth = value.clone().startOf('month')
            formik.setFieldValue(key, firstDayOfMonth)
          }}
          onBlur={formik.handleBlur}
          value={formik.values[key] ? moment(formik.values[key]) : undefined}
          disabledDate={(currentDate) => {
            const today = moment().startOf('month')

            return currentDate.isSameOrBefore(today, 'month')
          }}
          style={{ width: '100%' }}
        />
      )}
      {type === 'Number' && (
        <Input
          type="number"
          name={key}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values[key]}
          style={{ width: '100%' }}
        />
      )}
      {type === 'FileUpload' && (
        <Upload
          name={key}
          beforeUpload={(file) => {
            formik.setFieldValue(key, file)
            return false
          }}
          accept="application/pdf"
        >
          <div className="border border-dashed border-g400 h-12 w-full rounded-lg flex items-center justify-center cursor-pointer">
            <span className="hidden lg:block">Click here to browse</span>
            <span className="lg:hidden">Tap here to browse</span>
          </div>
        </Upload>
      )}
      {type === 'Message' && (
        <Alert message={description} type="info" showIcon />
      )}
      {key === 'phase_description' && (
        <MilestoneInput
          value={formik.values[key]}
          onChange={(val) => formik.setFieldValue(key, val)}
        />
      )}

      {formik.errors[key] && formik.touched[key] && (
        <div className="text-red-500">{formik.errors[key]}</div>
      )}

      {explanation && <Explanation explanation={explanation} />}
    </div>
  )
}

const Explanation = ({ explanation }) => {
  const [isShown, setIsShown] = useState(false)

  return (
    <div className="flex items-start gap-x-4">
      <QuestionCircleOutlined className="text-2xl mt-1 text-g400" />
      <div className="flex flex-col items-start">
        {isShown && <div dangerouslySetInnerHTML={{ __html: explanation }} />}

        <button
          className={cn(
            'bg-transparent outline-none underline font-semibold',
            isShown ? 'mt-4' : 'mt-1',
          )}
          onClick={() => setIsShown(!isShown)}
        >
          {isShown ? 'Hide tips' : 'View tips'}
        </button>
      </div>
    </div>
  )
}

export default QuestionView
